import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { apiClient } from '@/core/api/client';
import { Heart, ExternalLink } from 'lucide-react';
import { JourneyProgress } from './JourneyProgress';
import { UnderstandCareStage } from './UnderStandCareStage';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ResourceLinksSkeleton } from '@/features/resource-hub/components/ResourceLinksSkeleton';

type ChatResponsePayload = {
  journey_acknowledgment: string;
  journey_narrative: string;
  journey_highlight_quote: string;
  journey_closing: string;
};

type JourneySummaryRequest = {
  user_type: 'care_seeker' | 'advisor';
  owner_user_id: string;
  care_stage_id: string;
  session_id: string;
  conversation: { role: string; content: string }[];
  classification: {
    is_possible: boolean;
    care_stage: string;
    care_mode: string;
    summary: string;
    zip_code: string;
    budget_range: string;
  };
};

type JourneySummaryApiResponse = Partial<ChatResponsePayload> & {
  id?: string;
  plan_name?: string;
  owner_user_id?: string;
  session_id?: string;
  care_stage_id?: string;
  data?: Partial<ChatResponsePayload> & Partial<CareContextPayload>;
  result?: Partial<ChatResponsePayload> & Partial<CareContextPayload>;
  journey_summary?: Partial<ChatResponsePayload> & Partial<CareContextPayload>;
};

type SummaryQueryResult = {
  summary: ChatResponsePayload | null;
  carePlanId: string | null;
  careContext: CareContextPayload | null;
};

// type ResourceType = {
//   name?: string | null;
// };

type Resource = {
  id: string;
  title: string;
  seoUrl: string;
  category: string;
  type: string;
};

type ResourcesApiResponse = {
  resources?: Resource[];
};

type CareContextPayload = {
  session_id: string;
  care_stage_id: string;
};

type SituationSummaryLocationState = {
  sessionId?: string;
  careStage?: string;
};

const formatCareStageLabel = (careStage?: string): string | null => {
  if (!careStage) {
    return null;
  }

  return careStage
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getCareContext = (responseData: JourneySummaryApiResponse): CareContextPayload | null => {
  const payloadCandidates = [
    responseData,
    responseData.data,
    responseData.result,
    responseData.journey_summary,
  ];

  for (const payload of payloadCandidates) {
    if (payload?.session_id && payload.care_stage_id) {
      return {
        session_id: payload.session_id,
        care_stage_id: payload.care_stage_id,
      };
    }
  }

  return null;
};

const storeCareContext = async (payload: CareContextPayload): Promise<void> => {
  const { data } = await apiClient.post<CareContextPayload>(
    '/resources/store-care-context',
    payload
  );
  sessionStorage.setItem('bquest_care_stage_id', data.care_stage_id);
};

const getNormalizedSummary = (
  responseData: JourneySummaryApiResponse
): ChatResponsePayload | null => {
  const payloadCandidates = [
    responseData,
    responseData.data,
    responseData.result,
    responseData.journey_summary,
  ];

  const getFieldValue = (field: keyof ChatResponsePayload) => {
    for (const payload of payloadCandidates) {
      const value = payload?.[field];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  };

  const nextSummary = {
    journey_acknowledgment: getFieldValue('journey_acknowledgment'),
    journey_narrative: getFieldValue('journey_narrative'),
    journey_highlight_quote: getFieldValue('journey_highlight_quote'),
    journey_closing: getFieldValue('journey_closing'),
  };

  if (
    !nextSummary.journey_acknowledgment &&
    !nextSummary.journey_narrative &&
    !nextSummary.journey_highlight_quote &&
    !nextSummary.journey_closing
  ) {
    return null;
  }

  return nextSummary;
};

export function SituationSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  // const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const locationState = location.state as SituationSummaryLocationState | null;
  const routeSessionId = locationState?.sessionId;

  // Persist sessionId so it survives a hard page refresh
  if (routeSessionId) {
    sessionStorage.setItem('bquest_nav_session_id', routeSessionId);
  }
  const sessionId = routeSessionId ?? sessionStorage.getItem('bquest_nav_session_id') ?? undefined;
  const careStageLabel = formatCareStageLabel(locationState?.careStage);

  const { data: queryResult, isPending: isLoadingSummary } = useQuery<SummaryQueryResult>({
    queryKey: ['journey-summary', sessionId],
    enabled: !!sessionId,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      // Return cached result if available — avoids re-calling the AI (which
      // throws a duplicate-key error when the same session_id is submitted twice)
      const cacheKey = `bquest_journey_summary_${sessionId}`;
      const cached = sessionStorage.getItem(cacheKey);

      const { data: journeyPayload } = await apiClient.get<JourneySummaryRequest>(
        '/navigator/generate-journey-summary',
        { params: { session_id: sessionId } }
      );

      if (cached) {
        const cachedResult = JSON.parse(cached) as Partial<SummaryQueryResult>;
        const cachedCareStageId =
          cachedResult.careContext?.care_stage_id ?? journeyPayload.care_stage_id;
        if (cachedCareStageId) {
          sessionStorage.setItem('bquest_care_stage_id', cachedCareStageId);
        }
        return {
          summary: cachedResult.summary ?? null,
          carePlanId: cachedResult.carePlanId ?? null,
          careContext: cachedResult.careContext ?? null,
        };
      }

      const { data: responseData } = await apiClient.post<JourneySummaryApiResponse>(
        '/care-engine/journey-summary',
        journeyPayload,
      );
      const careContext = getCareContext(responseData);
      if (careContext) {
        await storeCareContext(careContext);
      }

      const result: SummaryQueryResult = {
        summary: getNormalizedSummary(responseData),
        carePlanId: responseData.id ?? null,
        careContext,
      };

      sessionStorage.setItem(cacheKey, JSON.stringify(result));
      return result;
    },
  });

  const {
    data: resources,
    isPending: isLoadingResources,
    isError: isResourcesError,
  } = useQuery<Resource[]>({
    queryKey: ['summary-resources', sessionId],
    // Only run after the journey-summary query completes and store-care-context has been called
    enabled: queryResult !== undefined,
    queryFn: async () => {
      const { data } = await apiClient.get<ResourcesApiResponse>('/resources/get-all-resources');

      return data.resources ?? [];
    },
  });

  const situationSummary = queryResult?.summary ?? null;
  const careStageId =
    queryResult?.careContext?.care_stage_id ?? sessionStorage.getItem('bquest_care_stage_id');
  const carePlanId = queryResult?.carePlanId ?? null;

  // const toggleSection = (sectionId: string) => {
  //   setExpandedSections((currentSections) => {
  //     const nextSections = new Set(currentSections);

  //     if (nextSections.has(sectionId)) {
  //       nextSections.delete(sectionId);
  //     } else {
  //       nextSections.add(sectionId);
  //     }

  //     return nextSections;
  //   });
  // };

  return (
    <div className="min-h-screen">
      <JourneyProgress currentStep="summary" />

      <div className="container max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          <div>
            <div className="mb-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#3d7872]/20 to-[#3d7872]/10 rounded-full mb-6">
                <Heart className="text-[#3d7872]" size={36} />
              </div>
              <h1 className="text-4xl font-semibold text-gray-900 mb-4">
                We Understand Your Journey
              </h1>
              {careStageLabel && (
                <div className="mb-4 inline-flex items-center rounded-full bg-[#3d7872]/10 px-4 py-2 text-sm font-medium text-[#2d6862]">
                  Your Care stage: {careStageLabel}
                </div>
              )}
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                {isLoadingSummary
                  ? 'Preparing your summary...'
                  : situationSummary?.journey_acknowledgment ||
                    'We could not load your journey summary right now.'}
              </p>

              {/* <div className="mt-6">
                <Button
                  variant="outline"
                  className="border-[#3d7872] text-[#3d7872] hover:bg-[#3d7872] hover:text-white"
                >
                  <Download size={16} className="mr-2" />
                  Download Care Summary
                </Button>
              </div> */}
            </div>

            <Card className="p-10 mb-10 bg-gradient-to-br from-white to-[#3d7872]/5 border-[#3d7872]/20">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">What You've Shared</h2>
              <div className="prose prose-lg max-w-none">
                {situationSummary ? (
                  <>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {situationSummary.journey_narrative}
                    </p>

                    <div className="bg-white/70 rounded-xl p-6 border border-[#3d7872]/10 my-6">
                      <p className="text-gray-700 leading-relaxed italic">
                        "{situationSummary.journey_highlight_quote}"
                      </p>
                    </div>

                    <p className="text-gray-700 leading-relaxed">
                      {situationSummary.journey_closing}
                    </p>
                  </>
                ) : (
                  <p className="text-gray-700 leading-relaxed">
                    Complete the chatbot journey to see your personalized summary here.
                  </p>
                )}
              </div>
            </Card>

            <div className="mb-12">
              <UnderstandCareStage careStageId={careStageId} />
            </div>

            <Card className="p-10 bg-gradient-to-br from-[#3d7872]/10 to-[#3d7872]/5 border-[#3d7872]/30 text-center">
              <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-2xl mx-auto">
                You're not walking this path alone. We're here to help you find the right people,
                specialists, caregivers, coordinators, and fellow travelers who understand this
                journey. Together, we'll build a network of support that honors your mother's
                dignity and your own wellbeing.
              </p>

              <p className="text-gray-600 mb-8">Let's take the next step together.</p>

              <Button
                onClick={() =>
                  navigate('/detailed-care-navigator/provider-overview', {
                    state: {
                      sessionId,
                      carePlanId,
                    },
                  })
                }
                className="bg-[#3d7872] hover:bg-[#2d6862] text-white px-10 py-6 text-lg rounded-xl"
                size="lg"
              >
                Meet Your Care Team
              </Button>
            </Card>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-8">
              <Card className="p-6 bg-white border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Essential Resources</h2>
                <p className="text-sm text-gray-600 mb-6">
                  Start here for expert guidance and support on your care journey.
                </p>

                <div className="space-y-3">
                  {isLoadingResources && (
                    <p className="text-sm text-gray-500">Loading resources...</p>
                  )}

                  {isResourcesError && (
                    <p className="text-sm text-red-600">Unable to load resources.</p>
                  )}

                  {!isLoadingResources && !isResourcesError && resources?.length === 0 && (
                    <p className="text-sm text-gray-500">No resources available.</p>
                  )}

                  {isLoadingResources ? (
                    <ResourceLinksSkeleton />
                  ) : (
                    resources?.map((resource) => (
                      <Link
                        key={resource.id}
                        to={`/resource-hub-detail/${resource.seoUrl}`}
                        state={{ from: location.pathname }}
                        className="block rounded-lg border border-gray-200 p-4 transition-all hover:border-[#3d7872] hover:bg-[#3d7872]/5 group"
                      >
                        <div className="mb-1 flex items-start justify-between">
                          <h3 className="text-sm font-semibold text-gray-900 transition-colors group-hover:text-[#3d7872]">
                            {resource.title}
                          </h3>

                          <ExternalLink
                            className="flex-shrink-0 text-gray-400 transition-colors group-hover:text-[#3d7872]"
                            size={14}
                          />
                        </div>

                        <p className="mb-1 line-clamp-2 text-xs text-gray-600">
                          {resource.category|| 'Care Resource'}
                        </p>

                        <span className="text-xs font-medium text-[#3d7872]">
                          {resource.type}
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
